import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import Typography from '@mui/material/Typography';
import { cardStyles, labelStyles } from 'components/@shared/TableRemotePagination/MobileTable/styles';
import { MobileTableComponents } from 'components/@shared/TableRemotePagination/MobileTable/types';
import { ITableHeadCell } from 'components/@shared/TableRemotePagination/types';
import { OrderValue, TableParams } from 'types/common';

export interface MobileTableProps<T> {
    rows: T[];
    columns: ITableHeadCell[];
    tableParams: TableParams;
    totalCount: number;
    rowsPerPageOptions?: number[];
    hidePagination?: boolean;
    components?: MobileTableComponents<T>;
    isRowCountDisabled?: boolean;
    defaultRowsPerPage?: number;
    onSort: (order: OrderValue, orderBy: string) => void;
    onSetPage: (page: number) => void;
    onSetRowsPerPage: (rowsPerPage: number) => void;
}

export const MobileTable = <T extends { id: string }>({
    rows = [],
    columns,
    totalCount,
    tableParams,
    components = {},
    rowsPerPageOptions = [5, 10, 25],
    hidePagination = false,
    isRowCountDisabled = false,
    defaultRowsPerPage,
    onSort,
    onSetPage,
    onSetRowsPerPage,
}: MobileTableProps<T>) => {
    const handleChangePage = (_: unknown, newPage: number) => {
        onSetPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        onSetRowsPerPage(parseInt(event.target.value, 10));
    };

    const handleRequestSort = (property: string) => (event: React.MouseEvent<unknown>) => {
        const isAsc = tableParams.orderBy === property && tableParams.sortOrder === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';
        onSort(newOrder, property);
    };

    return (
        <>
            <Stack gap={2}>
                {rows.map((row) => (
                    <Card key={row.id} sx={cardStyles}>
                        <Box display="flex" width="100%">
                            <Box flex={1}>
                                <Grid container spacing={1}>
                                    {columns.map((column) => (
                                        <Grid item xs={6} sm={4} md={3} key={column.field}>
                                            <TableSortLabel
                                                active={tableParams.orderBy === column.field && !column.disableSort}
                                                direction={
                                                    tableParams.orderBy === column.field ? tableParams.sortOrder : 'asc'
                                                }
                                                onClick={
                                                    column.disableSort ? undefined : handleRequestSort(column.field)
                                                }
                                                hideSortIcon={column.disableSort}
                                                sx={labelStyles}
                                            >
                                                <Typography
                                                    variant="overline"
                                                    fontWeight={500}
                                                    lineHeight="1rem"
                                                    color="primary.dark"
                                                    noWrap
                                                >
                                                    {column.label}
                                                </Typography>
                                            </TableSortLabel>
                                            <Box>
                                                {components.Cell ? (
                                                    <components.Cell rowData={row} column={column} />
                                                ) : (
                                                    <Typography>
                                                        {(row[column.field as keyof typeof row] as React.ReactNode) ||
                                                            '--'}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                            {components.Actions && (
                                <Box width={40}>
                                    <components.Actions rowData={row} />
                                </Box>
                            )}
                        </Box>
                    </Card>
                ))}
            </Stack>
            {!hidePagination && (
                <TablePagination
                    rowsPerPageOptions={rowsPerPageOptions}
                    component="div"
                    count={totalCount}
                    rowsPerPage={defaultRowsPerPage ?? tableParams.pageSize}
                    page={tableParams.page}
                    showFirstButton
                    showLastButton
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    slotProps={{ select: { disabled: isRowCountDisabled } }}
                    labelRowsPerPage="Rows per range:"
                />
            )}
        </>
    );
};
